// Removed unused chevrons/icons after switcher removal
import { Portal } from '@headlessui/react';
import classNames from 'classnames';
import {
  addDays,
  addMonths,
  areIntervalsOverlapping,
  endOfWeek,
  format,
  Interval,
  isSameMonth,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '~/components/Layout';
import { AddButton } from '~/components/ui/buttons/AddButton';
import type { Filter } from '~/components/ui/FilterGroup';
import { filterKeyValueSeperator } from '~/components/ui/FilterGroup';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { NavContainer } from '~/components/ui/NavContainer';
import { JobStatus, ScheduleQuery } from '~/gql/graphql';
import {
  extractAttributeFilterOptions,
  type ContainsAttributes,
} from '~/helpers/attributes';
import parseFilters from '~/helpers/parseFilters';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { useLocalStorage } from '~/hooks/useLocalStorage';
import { useScheduleRange } from '~/hooks/useScheduleRange';
import { useSearch } from '~/hooks/useSearch';
import { JobsNav } from '~/layouts/nav/JobsNav';
import { useSites } from '~/routes/resources/sites';

export type PlannerViewData = ScheduleQuery['sites'];

// Use the generated, typed document to ensure cache + exchanges work correctly

// Derived, precise types for schedule data
type ScheduleSite = NonNullable<PlannerViewData>[number];
type ScheduleEvent = ScheduleSite['calendar'][number];

// UI-augmented types used after client-side filtering/mapping
type AugmentedScheduleEvent = ScheduleEvent & { filtered: boolean };
type AugmentedScheduleSite = ScheduleSite & {
  calendar: AugmentedScheduleEvent[];
  top: boolean;
};
type AugmentedPlannerViewData = AugmentedScheduleSite[];

// Adapter to avoid unsafe casts when extracting attribute filter options
// Only passes through the attributes field which extractAttributeFilterOptions requires
type AttributeOptionSource = ContainsAttributes;
const toAttributeOptionSource = (
  sites: ScheduleSite[] = []
): AttributeOptionSource[] =>
  sites.map(({ attributes }) => ({
    attributes: attributes ?? null,
  }));

const SEARCH_OPTIONS = {
  keys: ['name', 'address', 'calendar.summary'],
};

// Static filter options to prevent recreation on every render
const DATE_RANGE_OPTIONS = [
  { value: 'endOfWeek', label: 'This week' },
  { value: '1', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: '3', label: 'Next 3 days' },
  { value: '7', label: 'Next 7 days' },
  { value: '14', label: 'Next 14 days' },
  { value: '30', label: 'Next 30 days' },
  { value: '365', label: 'Next 12 months' },
];

// Static job status options to prevent recreation on every render
const JOB_STATUS_OPTIONS = [
  { value: JobStatus.Created, label: 'Created' },
  { value: JobStatus.InProgress, label: 'In Progress' },
  { value: JobStatus.Offered, label: 'Offered' },
  { value: JobStatus.Accepted, label: 'Accepted' },
  { value: JobStatus.Declined, label: 'Declined' },
  { value: JobStatus.Cancelled, label: 'Cancelled' },
  { value: JobStatus.Complete, label: 'Complete' },
];

export function parseAttributeFilters(values: string[] | undefined) {
  return values
    ? values.reduce<{ id: string; value: string[] }[]>((acc, filter) => {
        const [key, ...value] = filter.split(filterKeyValueSeperator);
        const index = acc.findIndex((f) => f.id === key);
        if (index === -1) {
          acc.push({ id: key, value: value });
        } else {
          acc[index].value.push(...value);
        }
        return acc;
      }, [])
    : [];
}

// Helper function to check if an event should be filtered (shown with opacity)
function isEventFiltered(
  event: ScheduleEvent,
  eventLevelFilters: {
    status?: string[];
    assignee?: string[];
    included?: string[];
    tags?: string[];
  }
): boolean {
  // Skip filtering for booking events
  if (event.status === 'Booking') {
    return false;
  }

  // Early return if no event-level filters are active
  const hasEventFilters = Object.values(eventLevelFilters).some(
    (filter) => filter && (Array.isArray(filter) ? filter.length > 0 : true)
  );
  if (!hasEventFilters) {
    return false;
  }

  // Status filter
  if (
    eventLevelFilters.status &&
    !isValueAllowedByFilter(event.status, eventLevelFilters.status)
  ) {
    return true;
  }

  // Assignee filter
  if (
    eventLevelFilters.assignee &&
    !isValueAllowedByFilter(event.assignee?.id, eventLevelFilters.assignee)
  ) {
    return true;
  }

  // Included filter
  if (
    eventLevelFilters.included &&
    !isAnyValueAllowedByFilter(
      event.included?.map(({ id }: { id: string }) => id) ?? [],
      eventLevelFilters.included
    )
  ) {
    return true;
  }

  // Tags filter
  if (
    eventLevelFilters.tags &&
    !eventLevelFilters.tags.some((filterByTagId) =>
      event.tags?.includes(filterByTagId)
    )
  ) {
    return true;
  }

  return false;
}

// Helper functions for filtering
function isValueAllowedByFilter(
  fieldValue: string | null | undefined,
  filter: string[] | undefined
) {
  if (!filter) return true;
  return filter.length === 0 || filter.includes(fieldValue ?? '');
}

function isAnyValueAllowedByFilter(
  fieldValues: string[],
  filter: string[] | undefined
) {
  if (!filter) return true;
  return filter.length === 0 || fieldValues.some((val) => filter.includes(val));
}

function createFilterRange(value: string | undefined): Interval | undefined {
  if (!value) return undefined;

  const now = new Date();

  switch (value) {
    case 'endOfWeek':
      return { start: startOfDay(now), end: endOfWeek(now) };
    case 'tomorrow':
      return {
        start: startOfDay(addDays(now, 1)),
        end: startOfDay(addDays(now, 2)),
      };
    default:
      return {
        start: startOfDay(now),
        end: startOfDay(addDays(now, parseInt(value, 10))),
      };
  }
}

// Main filtering function - exactly like the old working route
function applyFilters(
  data: PlannerViewData | undefined,
  filters: Record<string, string[] | undefined> | undefined
): AugmentedPlannerViewData {
  if (!data) {
    return [];
  }

  if (!filters) {
    // Convert unfiltered data to filtered format
    return data.map<AugmentedScheduleSite>((site) => ({
      ...site,
      calendar: site.calendar.map<AugmentedScheduleEvent>((event) => ({
        ...event,
        filtered: false,
      })),
      top: true,
    }));
  }

  const checkIn = filters.checkIn?.[0];
  const checkOut = filters.checkOut?.[0];
  const checkInRange = createFilterRange(checkIn);
  const checkOutRange = createFilterRange(checkOut);

  const filterScheduled = filters.scheduled?.[0];
  const filterScheduledRange = createFilterRange(filterScheduled);

  const attributeFilters = parseAttributeFilters(filters['attributes']);

  // Event-level filters that should show events with opacity instead of hiding sites
  const eventLevelFilters = {
    status: filters.status,
    assignee: filters.assignee,
    included: filters.included,
    tags: filters.tags,
  };

  const result = data
    .filter((site: ScheduleSite) => {
      // Site-level filtering: locationName, attributes, and booking filters
      const passesLocationFilter = filters.locationName?.length
        ? filters.locationName.includes(site.name)
        : true;

      const passesAttributeFilter = attributeFilters.every(
        (attributeFilter) => {
          const attribute = site.attributes?.find(
            (attribute) => attribute.id === attributeFilter.id
          );
          if (!attribute) return false;
          return attributeFilter.value.every((value) =>
            attribute.value.includes(value)
          );
        }
      );

      const passesBookingFilter =
        (!checkInRange && !checkOutRange) ||
        site.calendar
          .filter((event: ScheduleEvent) => event.status === 'Booking')
          .some((event: ScheduleEvent) => {
            const match = Boolean(
              (checkInRange &&
                isWithinInterval(new Date(event.start), checkInRange)) ||
                (checkOutRange &&
                  isWithinInterval(
                    new Date(event.end || event.start),
                    checkOutRange
                  ))
            );
            return match;
          });

      // Scheduled filter logic (site-level) - filter sites based on scheduled jobs
      const passesScheduledFilter =
        !filterScheduledRange ||
        site.calendar
          .filter((event: ScheduleEvent) => event.status !== 'Booking') // Only check scheduled jobs, not bookings
          .some((event: ScheduleEvent) => {
            if (!event.start) return false; // Skip events without start date

            const eventScheduledRange = {
              start: new Date(event.start),
              end: event.end ? new Date(event.end) : new Date(event.start),
            };

            return areIntervalsOverlapping(
              eventScheduledRange,
              filterScheduledRange
            );
          });

      return (
        passesLocationFilter &&
        passesAttributeFilter &&
        passesBookingFilter &&
        passesScheduledFilter
      );
    })
    .map<AugmentedScheduleSite>((site) => {
      return {
        ...site,
        calendar: site.calendar.map<AugmentedScheduleEvent>((event) => ({
          ...event,
          filtered: isEventFiltered(event, eventLevelFilters),
        })),
        top: true,
      };
    });

  return result;
}

// Context for sharing calendar data across components
interface CalendarContextType {
  data?: AugmentedPlannerViewData;
  stableData?: ScheduleQuery;
  filtersValue?: URLSearchParams;
  setFiltersValue?: (value: URLSearchParams) => void;
  parsedFilters?: Record<string, string[] | undefined>;
  searchValue?: string;
  isLoading?: boolean;
  onPlaceClick?: (siteName: string) => void;
  /** Loaded date range bounds for the current data */
  loadedBounds?: { start: Date; end: Date } | null;
  /** Request to extend the loaded range in a direction */
  extendRange?: (direction: 'backward' | 'forward') => void;
  /**
   * Ensures the given date is within the loaded range.
   * For nearby dates: extends incrementally.
   * For far jumps: re-centers the range around the target date.
   */
  ensureDateInRange?: (date: Date) => boolean;
}

const CalendarContext = createContext<CalendarContextType>({});

export const useCalendarContext = () => {
  const context = useContext(CalendarContext);
  return context;
};

interface CalendarLayoutProps {
  children: React.ReactNode;
  // Note: retained for API compatibility; used in default view only
  showDateNavigation?: boolean;
  showFilterNavArrows?: boolean;
  scrollDate?: Date;
  onDateNavigate?: (action: 'today' | 'prev' | 'next') => void;
  viewType?: 'default' | 'calendar';
  calendarViewControls?: {
    currentDate: Date;
    onMonthSelect: (date: Date) => void;
    onTodayClick: () => void;
    onBackClick: () => void;
  };
}

export function CalendarLayout({
  children,
  onDateNavigate,
  viewType = 'default',
  calendarViewControls,
}: CalendarLayoutProps) {
  const prevDataRef = useRef<ScheduleQuery | undefined>(undefined);

  const { placeId } = useParams();
  const navigate = useNavigate();

  const { filterRowRef, dynamicMarginTop } = useDynamicHeaderMargin();
  const { t } = useTranslation(['translation', 'job']);
  const { isMobile } = useBreakpoint();
  const localStorage = useLocalStorage();
  const sites = useSites();

  const [searchValue, setSearchValue] = useState('');

  // Tenant-scoped filter storage (automatically scoped by useLocalStorage hook)
  const getStoredFilters = useCallback((): string => {
    try {
      return localStorage.get('form.filter.calendar') || '';
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
      return '';
    }
  }, [localStorage]);

  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(
    new URLSearchParams(getStoredFilters())
  );

  useEffect(() => {
    try {
      localStorage.set('form.filter.calendar', filtersValue.toString());
    } catch (error) {
      console.warn('Failed to write to localStorage:', error);
    }
  }, [filtersValue, localStorage]);

  // Memoize the string representation of filtersValue
  const filtersValueString = useMemo(
    () => filtersValue.toString(),
    [filtersValue]
  );

  // Memoize the parsed filters to prevent unnecessary recalculations
  const parsedFilters = useMemo(() => {
    return parseFilters(new URLSearchParams(filtersValueString));
  }, [filtersValueString]);

  // Fetch schedule data with range-aware loading
  // Initial load: 2 weeks before today + 2 months after
  // As user navigates, extends the range forward/backward
  const {
    data,
    fetching,
    error,
    loadedBounds,
    extendRange,
    ensureDateInRange,
  } = useScheduleRange({
    initialDaysBefore: 14,
    initialMonthsAfter: 2,
    extensionDaysBefore: 30,
    extensionDaysAfter: 60,
    extensionThreshold: 7,
  });

  // Stabilize GraphQL data reference to prevent unnecessary re-renders
  const stableData = useMemo<ScheduleQuery | undefined>(() => {
    // Only update if the actual content changed, not just the reference
    if (!prevDataRef.current || !data) {
      prevDataRef.current = data;
      return data;
    }

    // Quick check: if tags or assignees changed, data definitely changed
    if (
      prevDataRef.current?.tags?.nodes?.length !== data?.tags?.nodes?.length ||
      prevDataRef.current?.assignees?.length !== data?.assignees?.length
    ) {
      prevDataRef.current = data;
      return data;
    }

    // Shallow comparison first
    const prevSites = prevDataRef.current?.sites;
    const currentSites = data?.sites;

    if (prevSites === currentSites) {
      return prevDataRef.current;
    }

    // Fast length check
    if (prevSites?.length !== currentSites?.length) {
      prevDataRef.current = data;
      return data;
    }

    // Create lightweight hash of only the fields that actually change frequently
    const createSiteHash = (
      site: NonNullable<ScheduleQuery['sites']>[number]
    ) => {
      if (!site?.calendar) return `${site?.id}:0:`;

      // Helper to serialize assignee for hash
      const serializeAssignee = (
        assignee?: { id: string; name: string; image?: string | null } | null
      ): string => {
        if (!assignee) return '';
        return [assignee.id, assignee.name, assignee.image || ''].join(',');
      };

      // Helper to serialize tags for hash (tags affect filtering)
      const serializeTags = (tags?: string[] | null): string => {
        if (!tags || tags.length === 0) return '';
        return tags.sort().join(',');
      };

      // Hash fields that affect rendering and may change via mutations:
      // - id, status, dates, times: affect positioning and display
      // - assignee (id, name, image): displayed in pill (avatar) and affects filtering
      // - tags: affects filtered property (filtering logic)
      const eventHashes = site.calendar
        .map((event: ScheduleEvent) =>
          [
            event.id,
            event.status,
            event.summary,
            serializeAssignee(event.assignee),
            serializeTags(event.tags),
            event.start || '',
            event.end || '',
            event.scheduledStartTime || '',
            event.scheduledEndTime || '',
          ].join(':')
        )
        .join('|');

      return `${site.id}:${site.calendar.length}:${eventHashes}`;
    };

    const prevHash = prevSites?.map(createSiteHash).join('::') || '';
    const currentHash = currentSites?.map(createSiteHash).join('::') || '';

    if (prevHash === currentHash) {
      return prevDataRef.current; // Return stable reference
    }

    prevDataRef.current = data;
    return data;
  }, [data]);

  const isEmpty = useMemo(
    () => !fetching && !error && (stableData?.sites?.length ?? 0) === 0,
    [fetching, error, stableData?.sites]
  );

  // Persist last viewed site to localStorage when placeId changes
  // Decode placeId to ensure consistent storage (guards against double-encoding edge cases)
  useEffect(() => {
    if (viewType === 'calendar' && placeId) {
      let decodedPlaceId = placeId;
      try {
        // Only attempt decode if it looks URL-encoded (contains %)
        if (placeId.includes('%')) {
          decodedPlaceId = decodeURIComponent(placeId);
        }
      } catch {
        // Malformed encoding - use original value
        decodedPlaceId = placeId;
      }
      localStorage.set('calendar.lastViewedSite', decodedPlaceId);
    }
  }, [viewType, placeId, localStorage]);

  // Redirect calendar root (/jobs/calendar) to last viewed site or first site's calendar page
  useLayoutEffect(() => {
    // Only for calendar view and when no specific placeId is provided
    if (viewType !== 'calendar') return;
    if (placeId) return;
    // Wait for data to be available
    if (!stableData?.sites?.length) return;

    // Preserve hash during navigation (e.g., for deep links like #org-settings-open:referrals)
    const currentHash = window.location.hash;

    // First priority: last viewed site from localStorage
    const lastViewedSite = localStorage.get('calendar.lastViewedSite');
    if (lastViewedSite) {
      // Verify the site still exists
      const siteExists = stableData.sites.some(
        (site: { id: string; name: string }) =>
          site.id === lastViewedSite || site.name === lastViewedSite
      );
      if (siteExists) {
        navigate(
          `/jobs/calendar/${encodeURIComponent(lastViewedSite)}${currentHash}`,
          {
            replace: true,
          }
        );
        return;
      }
    }

    // Fallback: first site
    const firstSiteId = stableData.sites[0]?.id;
    if (firstSiteId) {
      navigate(
        `/jobs/calendar/${encodeURIComponent(firstSiteId)}${currentHash}`,
        {
          replace: true,
        }
      );
    }
  }, [viewType, placeId, stableData?.sites, navigate, fetching, localStorage]);

  // Note: previously used to gate rendering; no longer needed (kept as comment for reference)

  // Direct property access, no memo needed
  const { results, search } = useSearch(
    stableData?.sites || [],
    SEARCH_OPTIONS
  );

  // Apply the complete filtering logic from the working old route
  const filteredResults = useMemo(() => {
    return applyFilters(results, parsedFilters);
  }, [results, parsedFilters]);

  // Memoize expensive filter options to prevent recreation on every render
  const attributeOptions = useMemo(
    () =>
      extractAttributeFilterOptions(
        toAttributeOptionSource((stableData?.sites ?? []) as ScheduleSite[]),
        stableData?.assignees
      ),
    [stableData?.sites, stableData?.assignees]
  );

  const assigneeOptions = useMemo(() => {
    if (!stableData?.assignees) return [];
    return stableData.assignees.map(
      (assignee: { id: string; name: string }) => ({
        value: assignee.id,
        label: assignee.name,
      })
    );
  }, [stableData?.assignees]);

  const tagOptions = useMemo(() => {
    const localTags = stableData?.tags?.nodes ?? [];
    return localTags.map((tag: { id: string; name: string }) => ({
      value: tag.id,
      label: tag.name,
    }));
  }, [stableData?.tags?.nodes]);

  const locationNameOptions = useMemo(() => {
    if (!sites) return [];
    return sites
      .filter(({ status }) => status === 'Active')
      .map(({ name }) => ({
        value: name,
        label: name,
      }));
  }, [sites]);

  // Determine a place to show monthly view for: prefer current :placeId, otherwise first site
  // Keep memoized for potential future use; currently not used since switcher removed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const resolvedMonthlyPlaceId = useMemo(() => {
    const firstSiteId = stableData?.sites?.[0]?.id;
    return placeId || firstSiteId || '';
  }, [placeId, stableData?.sites]);

  // Removed view switcher navigation

  // Removed view switcher navigation

  // Firefox: globally allow drop by preventing default on dragover/drop
  useEffect(() => {
    const prevent = (e: DragEvent) => {
      try {
        e.preventDefault();
      } catch (error) {
        // Prevent default may fail in some environments; safe to ignore
        console.warn('drag/drop preventDefault failed', error);
      }
    };
    document.addEventListener('dragover', prevent, {
      passive: false,
    } as EventListenerOptions);
    document.addEventListener('drop', prevent, {
      passive: false,
    } as EventListenerOptions);
    return () => {
      document.removeEventListener('dragover', prevent as EventListener);
      document.removeEventListener('drop', prevent as EventListener);
    };
  }, []);

  // Date navigation handlers
  const today = useCallback(() => {
    onDateNavigate?.('today');
  }, [onDateNavigate]);

  // Removed prev/next controls

  // Handle place click for sidebar navigation - opens the monthly view
  const handlePlaceClick = useCallback(
    (siteName: string) => {
      navigate(`/jobs/calendar/${encodeURIComponent(siteName)}`);
    },
    [navigate]
  );

  // Memoized filters config (stabilizes props and avoids recreations each render)
  const filtersConfig = useMemo<Filter[]>(() => {
    if (viewType === 'calendar') {
      // Monthly view: no keyword search, specific filters only
      return [
        {
          name: 'locationName',
          label: String(t('site')),
          options: locationNameOptions,
          type: 'select' as const,
        },
        {
          name: 'checkIn',
          label: String(t('checkIn')),
          options: DATE_RANGE_OPTIONS,
          showLabel: 'always' as const,
          searchable: false,
          multiple: false,
          type: 'select' as const,
        },
        {
          name: 'checkOut',
          label: String(t('checkOut')),
          options: DATE_RANGE_OPTIONS,
          showLabel: 'always' as const,
          searchable: false,
          multiple: false,
          type: 'select' as const,
        },
        {
          name: 'scheduled',
          label: String(t('scheduledJobs')),
          options: DATE_RANGE_OPTIONS,
          showLabel: 'always' as const,
          searchable: false,
          multiple: false,
          type: 'select' as const,
        },

        'divider' as const,
        {
          name: 'assignee',
          label: String(t('assignee')),
          options: assigneeOptions,
          showLabel: 'always' as const,
          type: 'select' as const,
        },
        {
          name: 'included',
          label: String(t('included')),
          options: assigneeOptions,
          showLabel: 'always' as const,
          type: 'select' as const,
        },
        ...(tagOptions.length > 0
          ? [
              {
                name: 'tags',
                label: String(t('tag_plural')),
                options: tagOptions,
                type: 'select' as const,
              },
            ]
          : []),
      ];
    }

    // Default view: full filter set
    return [
      {
        name: 'locationName',
        label: String(t('site')),
        options: locationNameOptions,
        type: 'select' as const,
      },
      {
        name: 'attributes',
        label: String(t('placeAttributes')),
        options: attributeOptions,
        type: 'facetedSelect' as const,
        isAttributeFilter: true,
      },
      {
        name: 'checkIn',
        label: String(t('checkIn')),
        options: DATE_RANGE_OPTIONS,
        showLabel: 'always' as const,
        searchable: false,
        multiple: false,
        type: 'select' as const,
      },
      {
        name: 'checkOut',
        label: String(t('checkOut')),
        options: DATE_RANGE_OPTIONS,
        showLabel: 'always' as const,
        searchable: false,
        multiple: false,
        type: 'select' as const,
      },
      {
        name: 'scheduled',
        label: String(t('scheduledJobs')),
        options: DATE_RANGE_OPTIONS,
        showLabel: 'always' as const,
        searchable: false,
        multiple: false,
        type: 'select' as const,
      },
      'divider' as const,
      {
        name: 'assignee',
        label: String(t('assignee')),
        options: assigneeOptions,
        showLabel: 'always' as const,
        type: 'select' as const,
      },
      {
        name: 'included',
        label: String(t('included')),
        options: assigneeOptions,
        showLabel: 'always' as const,
        type: 'select' as const,
      },
      {
        name: 'status',
        label: String(t('status')),
        options: JOB_STATUS_OPTIONS,
        searchable: false,
        type: 'select' as const,
      },
      ...(tagOptions.length > 0
        ? [
            {
              name: 'tags',
              label: String(t('tag_plural')),
              options: tagOptions,
              type: 'select' as const,
            },
          ]
        : []),
    ];
  }, [
    viewType,
    t,
    assigneeOptions,
    attributeOptions,
    tagOptions,
    locationNameOptions,
  ]);

  return (
    <>
      <NavContainer
        nav={
          <JobsNav
            view='scheduled'
            monthlyControls={
              viewType === 'calendar' && calendarViewControls
                ? ({
                    currentDate: calendarViewControls.currentDate,
                    onMonthSelect: calendarViewControls.onMonthSelect,
                  } as const)
                : undefined
            }
          />
        }
        enableScrollDetection={false}
      >
        <NavContainer.FilterRow ref={filterRowRef}>
          <FilterGroupSearch
            mobileActionButton={{
              label: 'Today',
              onClick:
                viewType === 'calendar'
                  ? calendarViewControls?.onTodayClick || today
                  : today,
            }}
            viewSwitcher={undefined}
            hideKeywordSearch={false}
            filters={filtersConfig}
            value={{
              searchValue,
              filtersValue,
            }}
            onChange={({
              searchValue: nextSearchValue,
              filtersValue: nextFiltersValue,
            }) => {
              setSearchValue(nextSearchValue);
              search(nextSearchValue);
              setFiltersValue(nextFiltersValue);
            }}
          />

          {/* Monthly view month selector (desktop only; mobile uses TopBar) */}
          {viewType === 'calendar' && calendarViewControls && !isMobile && (
            <div className='flex w-full items-center justify-between gap-2 px-4 py-3 lg:ml-auto lg:w-auto lg:px-0 lg:py-0'>
              <MonthDropdown
                currentDate={calendarViewControls.currentDate}
                onMonthSelect={calendarViewControls.onMonthSelect}
              />
            </div>
          )}
        </NavContainer.FilterRow>
      </NavContainer>
      <div
        className='transition-all duration-200 ease-custom lg:mt-0'
        style={{
          marginTop: dynamicMarginTop ? dynamicMarginTop : undefined,
          // On mobile, ensure the content area fills the available viewport height
          // beneath the sticky nav/filters and above the fixed bottom mobile nav.
          // Use svh to handle mobile browser chrome and subtract the measured
          // mobile nav height provided via the CSS variable.
          height: isMobile
            ? `calc(100svh - ${
                typeof dynamicMarginTop === 'number'
                  ? `${dynamicMarginTop}px`
                  : dynamicMarginTop || '0px'
              } - var(--mobile-nav-height, 90.5px))`
            : undefined,
          maxHeight: isMobile
            ? `calc(100svh - ${
                typeof dynamicMarginTop === 'number'
                  ? `${dynamicMarginTop}px`
                  : dynamicMarginTop || '0px'
              } - var(--mobile-nav-height, 90.5px))`
            : undefined,
          overflow: isMobile ? 'hidden' : undefined,
        }}
      >
        {/* NOTE: Authorization intentionally disabled for calendar/planner views while pricing model is being finalized for non-owner tenants */}

        {isEmpty && <div className='p-4 text-gray-500'>{t('no_results')}</div>}
        <CalendarContext.Provider
          value={{
            data: filteredResults,
            stableData,
            filtersValue,
            setFiltersValue,
            parsedFilters,
            searchValue,
            // Avoid spinner flicker: treat as loading only when there is no cached data yet
            isLoading: fetching && !stableData,
            onPlaceClick: handlePlaceClick,
            // Range-aware loading: expose bounds and extension functions
            loadedBounds,
            extendRange,
            ensureDateInRange,
          }}
        >
          <Layout rsbSmall>{children}</Layout>
        </CalendarContext.Provider>
      </div>
      {/* Add button for creating Jobs from calendar views (one per view) */}
      {!isMobile && <AddButton label='Job' to='/jobs/new' />}
    </>
  );
}

// Month dropdown for monthly view navigation
type MonthDropdownProps = {
  currentDate: Date;
  onMonthSelect: (date: Date) => void;
  openUpwards?: boolean;
  shortMonth?: boolean;
  openLeftwards?: boolean;
  /**
   * Visual variant of the trigger button.
   * - topbar: used in mobile TopBar, white text, full month name
   * - toolbar: used in floating toolbar; on mobile shows stacked MMM/YY and primary text
   * - default: existing styling/behavior
   */
  variant?: 'topbar' | 'toolbar' | 'default';
};

export const MonthDropdown = React.memo<MonthDropdownProps>(
  ({
    currentDate,
    onMonthSelect,
    openUpwards = false,
    shortMonth = false,
    openLeftwards = false,
    variant = 'default',
  }: MonthDropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);
    const [menuPos, setMenuPos] = useState<{
      top: number;
      left: number;
    } | null>(null);

    // Generate month options to match available range (6 months before to 12 months after today)
    const monthOptions = useMemo(() => {
      const today = new Date();
      const options = [];

      // Same range as the calendar: 6 months before to 12 months after today
      for (let i = -6; i <= 12; i++) {
        options.push(addMonths(today, i));
      }

      return options;
    }, []); // No dependencies since we always use today as base

    const updateMenuPosition = useCallback(() => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const dropdownWidth = menuRef.current?.offsetWidth || 256; // w-64 default
      const dropdownHeight = menuRef.current?.offsetHeight || 320; // max-h-80 default
      const viewportLeft = window.scrollX;
      const viewportTop = window.scrollY;
      const maxLeft = viewportLeft + window.innerWidth - dropdownWidth - 8;
      let left: number;
      if (openLeftwards) {
        // Align dropdown to the left by anchoring its right edge to the trigger's right edge
        const desiredLeft = viewportLeft + rect.right - dropdownWidth;
        left = Math.max(viewportLeft + 8, Math.min(desiredLeft, maxLeft));
      } else {
        // Default: align dropdown's left edge with trigger's left edge
        left = Math.max(
          viewportLeft + 8,
          Math.min(viewportLeft + rect.left, maxLeft)
        );
      }
      let top: number;
      if (openUpwards) {
        // Position above the trigger; ensure at least 8px padding from top
        const desiredTop = viewportTop + rect.top - dropdownHeight - 8;
        top = Math.max(viewportTop + 8, desiredTop);
      } else {
        top = viewportTop + rect.bottom + 4;
      }
      setMenuPos({ top, left });
    }, [openUpwards, openLeftwards]);

    useEffect(() => {
      if (!isOpen) return;
      updateMenuPosition();
      // Focus first menu item when opened
      setTimeout(() => {
        const firstItem = menuRef.current?.querySelector(
          "button[role='menuitem']"
        ) as HTMLButtonElement | null;
        firstItem?.focus();
      }, 0);
      const onResize = () => updateMenuPosition();
      const onScroll = () => updateMenuPosition();
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          setIsOpen(false);
          requestAnimationFrame(() => {
            try {
              triggerRef.current?.focus();
            } catch {
              /* ignore focus errors */
            }
          });
        }
      };
      const onDocMouseDown = (e: MouseEvent) => {
        const target = e.target as Node | null;
        if (
          target &&
          !menuRef.current?.contains(target) &&
          !triggerRef.current?.contains(target)
        ) {
          setIsOpen(false);
          requestAnimationFrame(() => {
            try {
              triggerRef.current?.focus();
            } catch {
              /* ignore focus errors */
            }
          });
        }
      };
      window.addEventListener('resize', onResize);
      window.addEventListener('scroll', onScroll, true);
      document.addEventListener('keydown', onKeyDown as EventListener);
      document.addEventListener('mousedown', onDocMouseDown);
      return () => {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('scroll', onScroll, true);
        document.removeEventListener('keydown', onKeyDown as EventListener);
        document.removeEventListener('mousedown', onDocMouseDown);
      };
    }, [isOpen, updateMenuPosition]);

    return (
      <div className='relative'>
        {/* Month/Year dropdown */}
        <button
          id='month-dropdown-button'
          ref={triggerRef}
          aria-haspopup='menu'
          aria-expanded={isOpen}
          aria-controls='month-dropdown'
          onClick={() => setIsOpen(!isOpen)}
          className={classNames(
            'flex items-center gap-2 whitespace-nowrap rounded-full p-2 transition-colors md:pl-3',
            variant === 'topbar'
              ? '!text-white hover:bg-white/10'
              : 'text-primary hover:bg-gray-50'
          )}
        >
          {variant === 'toolbar' ? (
            <>
              <span className='flex leading-tight md:hidden'>
                <span className='text-sm font-semibold'>
                  {format(currentDate, 'MMM')} {'  '}{' '}
                  {format(currentDate, 'yy')}
                </span>
              </span>
              <span className='hidden text-lg font-semibold md:block'>
                {format(currentDate, shortMonth ? 'MMM yyyy' : 'MMMM yyyy')}
              </span>
            </>
          ) : (
            <span className='text-lg font-semibold'>
              {format(currentDate, shortMonth ? 'MMM yyyy' : 'MMMM yyyy')}
            </span>
          )}
          <svg
            className={classNames(
              'h-4 w-4',
              variant === 'toolbar' && 'hidden lg:block'
            )}
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M19 9l-7 7-7-7'
            />
          </svg>
        </button>

        {/* Dropdown menu via portal to escape stacking/overflow contexts */}
        {isOpen && menuPos && (
          <Portal>
            <div
              ref={menuRef}
              id='month-dropdown'
              role='menu'
              aria-labelledby='month-dropdown-button'
              className='z-[1600] max-h-80 w-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg'
              style={{
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  const items = menuRef.current?.querySelectorAll(
                    "button[role='menuitem']"
                  );
                  if (!items || items.length === 0) return;
                  const elements = Array.from(items) as HTMLButtonElement[];
                  const currentIndex = elements.findIndex(
                    (el) => el === document.activeElement
                  );
                  let nextIndex = 0;
                  if (e.key === 'ArrowDown') {
                    nextIndex =
                      currentIndex >= 0
                        ? (currentIndex + 1) % elements.length
                        : 0;
                  } else {
                    nextIndex =
                      currentIndex >= 0
                        ? (currentIndex - 1 + elements.length) % elements.length
                        : elements.length - 1;
                  }
                  elements[nextIndex]?.focus();
                  e.preventDefault();
                } else if (e.key === 'Home') {
                  const items = menuRef.current?.querySelectorAll(
                    "button[role='menuitem']"
                  );
                  if (!items || items.length === 0) return;
                  (items[0] as HTMLButtonElement).focus();
                  e.preventDefault();
                } else if (e.key === 'End') {
                  const items = menuRef.current?.querySelectorAll(
                    "button[role='menuitem']"
                  );
                  if (!items || items.length === 0) return;
                  (items[items.length - 1] as HTMLButtonElement).focus();
                  e.preventDefault();
                }
              }}
            >
              <div className='p-2'>
                {monthOptions.map((month) => {
                  const isTodayMonth = isSameMonth(month, new Date());

                  return (
                    <button
                      key={month.toISOString()}
                      role='menuitem'
                      tabIndex={-1}
                      onClick={() => {
                        onMonthSelect(month);
                        // close and return focus to trigger
                        setIsOpen(false);
                        requestAnimationFrame(() => {
                          try {
                            triggerRef.current?.focus();
                          } catch {
                            /* ignore focus errors */
                          }
                        });
                      }}
                      className={classNames(
                        'w-full rounded-md px-3 py-2 text-left text-sm',
                        isTodayMonth && 'bg-[#EB00FF1A] hover:bg-[#EB00FF1A]',
                        !isTodayMonth && 'hover:bg-gray-100'
                      )}
                      aria-current={isTodayMonth ? 'date' : undefined}
                    >
                      {format(month, shortMonth ? 'MMM yyyy' : 'MMMM yyyy')}
                    </button>
                  );
                })}
              </div>
            </div>
          </Portal>
        )}
      </div>
    );
  }
);

MonthDropdown.displayName = 'MonthDropdown';

// DateRangeNavButton removed (unused)
